import type { Document } from "@contentful/rich-text-types";
import type { SearchClient, SearchParams, SearchResponse } from "algoliasearch";
import { logger } from "../../common/logger";
import { DEFAULT_LOCALE, type SupportedLocaleType } from "../../common/types/languageTypes";
import type { Product } from "../../common/types/productTypes";
import {
   type AlgoliaProductHit,
   type AlgoliaRecipeHit,
   type AlgoliaRequestOptions,
   type AlgoliaV2_articleHit,
   type AllSearchTypes,
   type Localized,
   type RecipeSearchResults,
   SEARCH_TYPE,
   type SearchDomain,
   type SearchDomainType,
   type SearchResults,
   type V2ArticleSearchResults
} from "../../common/types/searchTypes";

export type SearchDomainWithAlgoliaClient = SearchDomain & { algoliaClient: SearchClient };

export type Explainer = {
   fields: {
      ingress: NorwegianString;
      topic: NorwegianString;
      explanation: Explanation;
   };
};

type NorwegianString = {
   "nb-NO": string;
};

export type Explanation = {
   "nb-NO": Document;
};

const CUSTOMER_GROUP_SALES_DATA_POSTFIX = "_customer_groups";

export const createProductAlgoliaFilter = (viewableSkus: string[], indexName: string, customerGroup: string) => {
   const productLimitations = viewableSkus.map((sku) => `sku:${sku}`).join(" OR ");

   logger.log("search", "Executing product search with viewable skus filter:", viewableSkus);

   // Only stores that use _customer_groups index should filter by customer group
   if (indexName.endsWith(CUSTOMER_GROUP_SALES_DATA_POSTFIX)) {
      const customerGroupClause =
         customerGroup === "default" ? "customerGroup:default" : `(customerGroup:${customerGroup} OR customerGroup:default)`;
      if (!productLimitations) {
         return customerGroupClause;
      }

      return `${customerGroupClause} AND (${productLimitations})`;
   }

   return productLimitations;
};

const createArticleAlgoliaFilter = (domainName: string) => {
   return `fields.searchCollections.${DEFAULT_LOCALE}: ${domainName}`;
};

const createRestrictSearchableAttributes = (
   searchDomain: SearchDomain,
   locale: SupportedLocaleType = "nb-NO"
): string[] | undefined => {
   if (searchDomain.type === "v2_article") {
      return ["title", "preface", "body"].map((name) => `fields.${name}.${locale}`);
   }
   return undefined;
};

const createAlgoliaSearchParams = (
   searchDomain: SearchDomain,
   searchType: AllSearchTypes,
   customerGroup: string,
   locale: SupportedLocaleType,
   viewableSkus: string[],
   algoliaUserToken: string | undefined,
   hitsPerPage = 6
): SearchParams => {
   const clickAnalytics = searchType !== "direct";
   const filters: string = (() => {
      switch (searchDomain.type) {
         case "product":
            return createProductAlgoliaFilter(viewableSkus, searchDomain.indexName, customerGroup);
         case "v2_article":
            return createArticleAlgoliaFilter(searchDomain.name);
         case "recipe":
            return createArticleAlgoliaFilter("oppskrifter");
      }
      return "";
   })();
   const restrictSearchableAttributes = createRestrictSearchableAttributes(searchDomain, locale);

   const requestOptions: AlgoliaRequestOptions = {
      clickAnalytics,
      filters,
      hitsPerPage: searchType === SEARCH_TYPE.Full ? 1000 : hitsPerPage
   };

   if (restrictSearchableAttributes) {
      requestOptions.restrictSearchableAttributes = restrictSearchableAttributes;
   }

   if (algoliaUserToken) {
      requestOptions.userToken = algoliaUserToken;
   }

   return requestOptions;
};

export const searchInDomain = async (
   query: string,
   searchDomain: SearchDomainWithAlgoliaClient,
   searchType: AllSearchTypes,
   customerGroup: string,
   algoliaUserToken: string | undefined,
   prioritizeAgreementAssortment: boolean,
   viewableSkus: string[],
   resolveProducts: (skus: string[]) => Product[],
   filterHitsForSegment: (items: AlgoliaV2_articleHit[]) => AlgoliaV2_articleHit[],
   locale: SupportedLocaleType = "nb-NO"
): Promise<SearchResults> => {
   if (searchDomain.type === "product") {
      const hitsPerPage = prioritizeAgreementAssortment ? 1000 : 100;
      const searchParams: SearchParams = createAlgoliaSearchParams(
         searchDomain,
         searchType,
         customerGroup,
         locale,
         viewableSkus,
         algoliaUserToken,
         hitsPerPage
      );
      const response: SearchResponse<AlgoliaProductHit> = await searchDomain.algoliaClient.searchSingleIndex<AlgoliaProductHit>({
         indexName: searchDomain.indexName,
         searchParams: { ...searchParams, query }
      });

      const products = resolveProducts(response.hits.map((h) => h.sku));

      const hits = products
         .map((product) => response.hits.find((hit) => product.sku === hit.sku))
         .filter((hit): hit is AlgoliaProductHit => !!hit);

      return {
         name: searchDomain.name,
         searchDomainType: "product",
         hits,
         queryID: response.queryID || "",
         products,
         query
      };
   }

   if (searchDomain.type === "v2_article") {
      const searchParams = createAlgoliaSearchParams(
         searchDomain,
         searchType,
         customerGroup,
         locale,
         viewableSkus,
         algoliaUserToken,
         2
      );
      const response = await searchDomain.algoliaClient.searchSingleIndex<AlgoliaV2_articleHit>({
         indexName: searchDomain.indexName,
         searchParams: { ...searchParams, query }
      });
      const hits = filterHitsForSegment(response.hits);
      const results: V2ArticleSearchResults = {
         name: searchDomain.name,
         searchDomainType: "v2_article",
         hits,
         queryID: response.queryID || "",
         query,
         data: []
      };
      if (results.hits.length) {
         results.data = hits.map((hit) => ({
            id: hit.objectID,
            imageId: hit.fields?.headerImage?.["nb-NO"]?.sys.id || "",
            callToAction: hit.fields?.callToAction?.["nb-NO"] || hit.fields?.title?.["nb-NO"] || "",
            subTitle: hit.fields?.callToActionSubTitle?.["nb-NO"]
         }));
      }
      return results;
   }

   if (searchDomain.type === "recipe") {
      const searchParams = createAlgoliaSearchParams(
         searchDomain,
         searchType,
         customerGroup,
         locale,
         viewableSkus,
         algoliaUserToken,
         2
      );
      const response = await searchDomain.algoliaClient.searchSingleIndex<AlgoliaRecipeHit>({
         indexName: searchDomain.indexName,
         searchParams: { ...searchParams, query }
      });
      const hits = [...response.hits];
      const results: RecipeSearchResults = {
         name: searchDomain.name,
         searchDomainType: "recipe",
         hits,
         queryID: response.queryID || "",
         query,
         data: []
      };
      if (results.hits.length) {
         // Recipes are only available in Norwegian, so we use "nb-NO" locale for fields
         results.data = results.hits.map((hit) => ({
            id: hit.objectID,
            recipe: hit.fields?.recipeData?.["nb-NO"],
            title: hit.fields?.title?.["nb-NO"]
         }));
      }
      return results;
   }

   return {
      name: searchDomain.name,
      searchDomainType: searchDomain.type,
      hits: [],
      queryID: "",
      query
   };
};

export const getLocalizedValue = <T>(obj: Localized<T>, preferredLocale: SupportedLocaleType): T | undefined =>
   (preferredLocale in obj ? obj[preferredLocale] : undefined) || obj[DEFAULT_LOCALE];

export const getLocalizedString = getLocalizedValue<string>;

export const getMaxHitsForSearchDomain = (
   searchDomainType: SearchDomainType,
   resultsWithHits: SearchResults[],
   isLg: boolean,
   isXl: boolean
) => {
   const results = resultsWithHits.find((result) => result.searchDomainType === searchDomainType);
   if (results) {
      if (searchDomainType === "product") {
         if (isXl) {
            return resultsWithHits.length > 1 ? 12 : 15;
         }
         if (isLg) {
            return resultsWithHits.length > 1 ? 9 : 12;
         }
         return resultsWithHits.length > 1 ? 5 : 8;
      }
      const productResults = resultsWithHits.find((result) => result.searchDomainType === "product");
      const isNarrow = productResults ? resultsWithHits.length > 2 : resultsWithHits.length > 1;
      if (productResults) {
         if (isNarrow) {
            return 2;
         }
         return isXl ? 6 : 3;
      }
      if (isNarrow) {
         return 4;
      }
      return 8;
   }
   return 0;
};
